import { useState } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  Chip,
  TextField,
  Modal,
  Stack,
  Button,
} from "@mui/material";
import { SimpleUserInfo, OtherUsers } from "types";
import VisAvatar from "@components/Avatar";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchField from "@components/SearchField";
import CloseIcon from "@mui/icons-material/Close";

export type VTAttendees = {
  selectedMeetingAttendees: any[];
  setSelectedMeetingAttendees: (selectedMeetingAttendees: any[]) => void;
  users: OtherUsers[];
  setUsers: (users: []) => void;
};

const Attendees = ({
  selectedMeetingAttendees,
  setSelectedMeetingAttendees,
  users,
  setUsers,
}: VTAttendees) => {
  const [attendeeModalIsOpen, setAttendeeModalIsOpen] =
    useState<boolean>(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [filteredResults, setFilteredResults] = useState<OtherUsers[]>([]);

  const searchedUsers = (searchValue: string) => {
    setSearchValue(searchValue);
    if (searchValue !== "") {
      const filteredData = users.filter((user) => {
        return Object.values(user)
          .join("")
          .toLowerCase()
          .includes(searchValue.toLowerCase());
      });
      setFilteredResults(filteredData);
    } else {
      setFilteredResults(users);
    }
  };

  const handleAddingAttendee = (user: OtherUsers) => {
    setSelectedMeetingAttendees([...selectedMeetingAttendees, user]);
    setSearchValue("");
  };

  const handleRemovingAttendee = (user: OtherUsers) => {
    setSelectedMeetingAttendees(
      selectedMeetingAttendees.filter(
        (attendee) => attendee.user_id !== user.user_id
      )
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          marginTop: "15px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontWeight: "700",
              fontSize: "16px",
              lineHeight: "21.82px",
              color: "#031C3A",
            }}
          >
            Attendees
          </Typography>

          {selectedMeetingAttendees?.length ? (
            <Box sx={{ display: "flex", width: "100%" }}>
              <Box
                sx={{
                  width: "100%",
                  maxHeight: "100px",
                  height: "100%",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "0.6em",
                    marginRight: "20px",
                    marginTop: "20px",
                    backgroundColor: "#F1F1F1",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-track": {
                    boxShadow: "1 1 1 6px #F1F1F1",
                    webkitBoxShadow: "1 1 1 6px #F1F1F1",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#C4C4C4",
                    borderRadius: "10px",
                  },
                }}
              >
                {selectedMeetingAttendees?.map((attendee) => (
                  <Chip
                    sx={{
                      margin: "5px",
                      background: "rgba(18, 113, 238, 0.05);",
                      border: "1px solid rgba(18, 113, 238, 0.05)",
                    }}
                    key={attendee.id}
                    label={attendee.name}
                    onDelete={() => {
                      handleRemovingAttendee(attendee);
                    }}
                    deleteIcon={
                      <CloseIcon
                        style={{ color: "var(--visionable-mid-blue)" }}
                      />
                    }
                  />
                ))}
              </Box>
              <AddIcon
                style={{
                  color: "var(--visionable-mid-blue",
                  cursor: "pointer",
                }}
                onClick={() => setAttendeeModalIsOpen(true)}
              />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ color: "#CCCCCC" }}>
                  No particpants added
                </Typography>
                <AddIcon
                  style={{
                    color: "var(--visionable-mid-blue",
                    cursor: "pointer",
                  }}
                  onClick={() => setAttendeeModalIsOpen(true)}
                />
              </Box>
            </>
          )}

          {attendeeModalIsOpen ? (
            <Modal hideBackdrop open={attendeeModalIsOpen}>
              <Box
                sx={{
                  position: "absolute" as "absolute",
                  right: 0,
                  width: { xs: "100%", md: "75%", lg: "65%", xl: "40%" },
                  height: "100%",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  p: 4,
                  overflow: "hidden",
                }}
              >
                <Stack spacing={4} sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <ArrowBackIosNewIcon
                      sx={{
                        fontSize: "16px",
                        fontWeight: "700",
                        marginRight: "30px",
                        color: "var(--visionable-dark-blue)",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setSelectedMeetingAttendees([]);
                        setAttendeeModalIsOpen(false);
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "32px",
                        fontWeight: "700",
                        lineHeight: "29px",
                        fontStyle: "normal",
                      }}
                    >
                      Select attendees
                    </Typography>
                  </Box>

                  <SearchField
                    placeholder="Search Directory"
                    value={searchValue}
                    onChange={(e) => searchedUsers(e.target.value)}
                  />

                  {selectedMeetingAttendees.length ? (
                    <Box
                      sx={{
                        maxHeight: "150px",
                        height: "100%",
                        overflowY: "scroll",
                        "&::-webkit-scrollbar": {
                          width: "0em",
                          marginRight: "20px",
                          marginTop: "20px",
                          backgroundColor: "#F1F1F1",
                          borderRadius: "10px",
                        },
                        "&::-webkit-scrollbar-track": {
                          boxShadow: "1 1 1 6px #F1F1F1",
                          webkitBoxShadow: "1 1 1 6px #F1F1F1",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#C4C4C4",
                          borderRadius: "10px",
                        },
                      }}
                    >
                      {selectedMeetingAttendees.map((attendee, index) => (
                        <Chip
                          sx={{
                            margin: "5px",
                            background: "rgba(18, 113, 238, 0.05);",
                            border: "1px solid rgba(18, 113, 238, 0.05)",
                          }}
                          key={attendee.id}
                          label={attendee.name}
                          onDelete={() => {
                            handleRemovingAttendee(attendee);
                          }}
                          deleteIcon={
                            <CloseIcon
                              style={{ color: "var(--visionable-mid-blue)" }}
                            />
                          }
                        />
                      ))}
                    </Box>
                  ) : (
                    <></>
                  )}

                  <Box
                    sx={{
                      width: "100%",
                      height:
                        selectedMeetingAttendees.length > 0 ? "500px" : "700px",
                      backgroundColor: "var(--visionable-background-gray)",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        width: "98%",
                        height: "90%",
                        overflowY: "scroll",
                        justifyContent: "flex-start",
                        "&::-webkit-scrollbar": {
                          width: "0.6em",
                          marginRight: "20px",
                          marginTop: "20px",
                          backgroundColor: "#F1F1F1",
                          borderRadius: "10px",
                        },
                        "&::-webkit-scrollbar-track": {
                          boxShadow: "1 1 1 6px #F1F1F1",
                          webkitBoxShadow: "1 1 1 6px #F1F1F1",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#C4C4C4",
                          borderRadius: "10px",
                        },

                        flexDirection: "column",
                      }}
                    >
                      {searchValue.length > 1 ? (
                        filteredResults.map((user) => {
                          return (
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  marginBottom: "10px",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    paddingLeft: "30px",
                                    paddingTop: "5px",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleAddingAttendee(user)}
                                >
                                  <VisAvatar userID={user.user_id} />
                                  <Typography
                                    sx={{
                                      fontSize: "16px",
                                      fontWeight: "700",
                                      lineHeight: "21.82px",
                                      color: "#031C3A",
                                      marginLeft: "10px",
                                    }}
                                  >
                                    {user.name}
                                  </Typography>
                                </Box>
                              </Box>
                            </>
                          );
                        })
                      ) : (
                        <>
                          <Box>
                            {users.map((user) => {
                              return (
                                <>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        paddingLeft: "30px",
                                        paddingTop: "5px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() => handleAddingAttendee(user)}
                                    >
                                      <VisAvatar userID={user.user_id} />
                                      <Typography
                                        sx={{
                                          fontSize: "16px",
                                          fontWeight: "700",
                                          lineHeight: "21.82px",
                                          color: "#031C3A",
                                          marginLeft: "10px",
                                        }}
                                      >
                                        {user.name}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </>
                              );
                            })}
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "10px",
                  }}
                >
                  <Button
                    size="large"
                    variant="contained"
                    sx={{
                      width: "137px",
                      height: "50px",
                      background: "var(--visionable-mid-blue)",
                      borderRadius: "4px",
                      marginRight: "10px",
                    }}
                    onClick={() => {
                      setAttendeeModalIsOpen(false);
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            </Modal>
          ) : (
            <></>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Attendees;
